import { createContext, useContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext(null);

const buildMuiTheme = () =>
  createTheme({
    palette: {
      mode: 'light',
      primary:    { main: '#16a34a', dark: '#15803d', light: '#22c55e', contrastText: '#fff' },
      secondary:  { main: '#22c55e' },
      background: { default: '#f4f7f4', paper: '#ffffff' },
      text: { primary: '#0a0f0a', secondary: '#3d5245', disabled: '#94a3b8' },
      divider: 'rgba(34,197,94,.15)',
      error:   { main: '#ef4444' },
      warning: { main: '#d97706' },
      success: { main: '#16a34a' },
      info:    { main: '#0ea5e9' },
    },
    typography: {
      fontFamily: '"Plus Jakarta Sans", "Inter", "DM Sans", sans-serif',
      h1: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 800, letterSpacing: '-0.03em' },
      h2: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 800, letterSpacing: '-0.02em' },
      h3: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
      h4: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700 },
      h5: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700 },
      h6: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700 },
      button: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600, letterSpacing: '0.01em', textTransform: 'none' },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: '#f4f7f4',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 9, padding: '10px 24px', fontSize: '0.9rem', fontWeight: 600 },
          containedPrimary: {
            background: 'linear-gradient(135deg, #22c55e, #16a34a)',
            color: '#fff',
            boxShadow: '0 4px 12px rgba(34,197,94,.25)',
            '&:hover': {
              background: 'linear-gradient(135deg, #16a34a, #15803d)',
              boxShadow: '0 6px 18px rgba(34,197,94,.32)',
            },
          },
          outlinedPrimary: {
            borderColor: 'rgba(34,197,94,.4)',
            borderWidth: '1.5px',
            color: '#16a34a',
            '&:hover': { borderColor: '#22c55e', background: 'rgba(34,197,94,.05)', borderWidth: '1.5px' },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 9,
              backgroundColor: '#f8fafc',
              '& fieldset': { borderColor: 'rgba(34,197,94,.2)' },
              '&:hover fieldset': { borderColor: 'rgba(34,197,94,.45)' },
              '&.Mui-focused fieldset': { borderColor: '#16a34a', borderWidth: 2 },
              '&.Mui-focused': { backgroundColor: 'rgba(34,197,94,.03)', boxShadow: '0 0 0 4px rgba(34,197,94,.08)' },
            },
            '& .MuiInputLabel-root': { color: '#64748b' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#16a34a' },
            '& input': { color: '#0a0f0a' },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 14, border: '1px solid rgba(34,197,94,.12)', backgroundImage: 'none', boxShadow: '0 2px 12px rgba(34,197,94,.06)' },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 7 },
          colorPrimary: { background: 'rgba(34,197,94,.12)', color: '#15803d', fontWeight: 600 },
          colorSuccess: { background: 'rgba(34,197,94,.12)', color: '#15803d', fontWeight: 600 },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'rgba(255,255,255,.88)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(34,197,94,.12)',
            boxShadow: '0 1px 16px rgba(34,197,94,.07)',
            color: '#0a0f0a',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: '#16a34a' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none', fontWeight: 600,
            '&.Mui-selected': { color: '#16a34a' },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-root': {
              color: '#16a34a', fontWeight: 700,
              borderBottom: '1px solid rgba(34,197,94,.15)',
              background: 'rgba(34,197,94,.04)',
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { background: 'rgba(34,197,94,.03)' },
            '& .MuiTableCell-root': { borderColor: 'rgba(34,197,94,.1)', color: '#0a0f0a' },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: 'rgba(34,197,94,.15)' },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            border: '1px solid rgba(34,197,94,.15)',
            boxShadow: 'none',
            '&:before': { display: 'none' },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          primary: {
            background: 'linear-gradient(135deg, #22c55e, #16a34a)',
            color: '#fff',
            boxShadow: '0 4px 16px rgba(34,197,94,.3)',
            '&:hover': { background: 'linear-gradient(135deg, #16a34a, #15803d)' },
          },
        },
      },
    },
  });

export const ThemeProvider = ({ children }) => {
  const [mode] = useState('light');
  const theme = useMemo(() => buildMuiTheme(mode), [mode]);

  return (
    <ThemeContext.Provider value={{ mode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
export default ThemeContext;